import React, { useCallback, useEffect, useMemo, useState, createContext, useContext } from 'react';
import type { Product } from '../products';
export type CartLine = {
  productId: string;
  qty: number;
};
export type BulkTier = {
  qty: number;
  discount: number; // 0..1
  label: string;
  savingsLabel: string | null;
};
export const BULK_TIERS: BulkTier[] = [{
  qty: 1,
  discount: 0,
  label: '1 vial',
  savingsLabel: null
}, {
  qty: 3,
  discount: 0.1,
  label: '3 vials',
  savingsLabel: 'Save 10%'
}, {
  qty: 5,
  discount: 0.15,
  label: '5 vials',
  savingsLabel: 'Save 15%'
}, {
  qty: 10,
  discount: 0.2,
  label: '10 vials',
  savingsLabel: 'Save 20%'
}];
export function getDiscountForQty(qty: number): number {
  let d = 0;
  for (const t of BULK_TIERS) {
    if (qty >= t.qty) d = t.discount;
  }
  return d;
}
type CartContextValue = {
  lines: CartLine[];
  totalCount: number;
  subtotal: number;
  totalSavings: number;
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  addItem: (productId: string, qty?: number) => void;
  setQty: (productId: string, qty: number) => void;
  removeItem: (productId: string) => void;
  clear: () => void;
  getLineTotal: (productId: string, unitPrice: number) => {
    gross: number;
    net: number;
    discount: number;
  };
};
const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = 'peptides-ml-cart-v1';
export function CartProvider({
  children,
  products
}: {
  children: React.ReactNode;
  products: Product[];
}) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) as CartLine[] : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      /* noop */
    }
  }, [lines]);
  const productMap = useMemo(() => {
    const m = new Map<string, Product>();
    products.forEach(p => m.set(p.id, p));
    return m;
  }, [products]);
  const addItem = useCallback((productId: string, qty: number = 1) => {
    setLines(prev => {
      const existing = prev.find(l => l.productId === productId);
      if (existing) {
        return prev.map(l => l.productId === productId ? {
          ...l,
          qty: Math.min(99, l.qty + qty)
        } : l);
      }
      return [...prev, {
        productId,
        qty: Math.min(99, qty)
      }];
    });
    setIsOpen(true);
  }, []);
  const setQty = useCallback((productId: string, qty: number) => {
    setLines(prev => qty <= 0 ? prev.filter(l => l.productId !== productId) : prev.map(l => l.productId === productId ? {
      ...l,
      qty: Math.min(99, qty)
    } : l));
  }, []);
  const removeItem = useCallback((productId: string) => {
    setLines(prev => prev.filter(l => l.productId !== productId));
  }, []);
  const clear = useCallback(() => setLines([]), []);
  const getLineTotal = useCallback((productId: string, unitPrice: number) => {
    const line = lines.find(l => l.productId === productId);
    const qty = line?.qty ?? 0;
    const gross = unitPrice * qty;
    const discount = getDiscountForQty(qty);
    const net = gross * (1 - discount);
    return {
      gross,
      net,
      discount
    };
  }, [lines]);
  const {
    totalCount,
    subtotal,
    totalSavings
  } = useMemo(() => {
    let count = 0;
    let gross = 0;
    let net = 0;
    for (const line of lines) {
      const p = productMap.get(line.productId);
      if (!p) continue;
      const d = getDiscountForQty(line.qty);
      const lineGross = p.price * line.qty;
      const lineNet = lineGross * (1 - d);
      count += line.qty;
      gross += lineGross;
      net += lineNet;
    }
    return {
      totalCount: count,
      subtotal: net,
      totalSavings: gross - net
    };
  }, [lines, productMap]);
  const value: CartContextValue = {
    lines,
    totalCount,
    subtotal,
    totalSavings,
    isOpen,
    openDrawer: () => setIsOpen(true),
    closeDrawer: () => setIsOpen(false),
    addItem,
    setQty,
    removeItem,
    clear,
    getLineTotal
  };
  return <CartContext.Provider value={value} data-id="element-36">{children}</CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}